/**
 * 워크스페이스 디자인 언어의 작은 부품 — 모양은 styles/workspace.css 가 정본이다.
 * 대시보드·칸반·앱 틀이 함께 쓴다. 여기에는 «무엇을 그릴지» 만 두고 색·크기 값은 두지 않는다.
 */
import React from 'react';

export type WsTone = 'red' | 'amber' | 'yellow' | 'green' | 'blue' | 'purple' | 'orange' | 'gray' | 'ink';

/** tone → 클래스. 그 클래스가 --tone / --tone-ink / --tone-bg / --tone-line 을 준다 */
export const wsTone = (tone?: WsTone | null) => (tone ? `ws-tone-${tone}` : '');

const AVATAR_TONES: WsTone[] = ['blue', 'purple', 'green', 'amber', 'orange', 'gray'];

/** 이름 → 동그라미 글자. 한글 이름은 성을 뺀 두 글자(«김민지» → «민지»), 영문은 머리글자 둘 */
export function wsInitials(name?: string | null): string {
  const n = (name || '').trim();
  if (!n) return '?';
  if (/[가-힣]/.test(n)) {
    const han = n.replace(/\s+/g, '');
    return han.length >= 3 ? han.slice(-2) : han.slice(0, 2);
  }
  const parts = n.split(/[\s._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : n.slice(0, 2)).toUpperCase();
}

/** 같은 이름은 늘 같은 색 */
export function wsAvatarTone(name?: string | null): WsTone {
  const s = name || '';
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length];
}

export const WsAvatar: React.FC<{ name?: string | null; size?: 'sm' | 'md' | 'lg'; tone?: WsTone; title?: string }> = ({
  name, size = 'md', tone, title,
}) => (
  <span
    className={`ws-avatar ${size !== 'md' ? `ws-avatar--${size}` : ''} ${wsTone(tone ?? wsAvatarTone(name))}`}
    title={title ?? name ?? undefined}
    aria-label={title ?? name ?? undefined}
  >
    {wsInitials(name)}
  </span>
);

/** 겹친 동그라미 — max 를 넘으면 «+n» */
export const WsAvatars: React.FC<{ names: Array<string | null | undefined>; max?: number; size?: 'sm' | 'md' | 'lg' }> = ({
  names, max = 3, size = 'md',
}) => {
  const list = names.filter((n): n is string => !!n && !!n.trim());
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const rest = list.length - shown.length;
  return (
    <span className="ws-avatars" aria-label={list.join(', ')}>
      {shown.map((n) => <WsAvatar key={n} name={n} size={size} />)}
      {rest > 0 && <span className={`ws-avatar ${size !== 'md' ? `ws-avatar--${size}` : ''} ws-tone-gray`}>+{rest}</span>}
    </span>
  );
};

export const WsPill: React.FC<{ tone?: WsTone; size?: 'sm' | 'md'; icon?: React.ReactNode; children: React.ReactNode; title?: string }> = ({
  tone = 'gray', size = 'md', icon, children, title,
}) => (
  <span className={`ws-pill ${size === 'sm' ? 'ws-pill--sm' : ''} ${wsTone(tone)}`} title={title}>
    {icon}
    {children}
  </span>
);

/**
 * 고리 — value(0~100) 만큼 진한 호, 그 끝에 accent 만큼 색 호를 잇는다.
 * value 가 null 이면 점선 빈 고리(«아직 시작 전»).
 */
export const WsRing: React.FC<{
  value: number | null;
  accent?: number;
  accentTone?: WsTone;
  size?: number;
  label?: React.ReactNode;
  title?: string;
}> = ({ value, accent = 0, accentTone = 'amber', size = 46, label, title }) => {
  if (value == null) {
    return (
      <span className="ws-ring ws-ring--empty" style={{ width: size, height: size }} title={title}>
        <span>{label ?? '–'}</span>
      </span>
    );
  }
  const stroke = 3.5;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const main = Math.max(0, Math.min(100, value));
  const tail = Math.max(0, Math.min(100 - main, accent));
  return (
    <span className="ws-ring" style={{ width: size, height: size }} title={title}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: 'var(--ws-line)' }} strokeWidth={stroke} />
        {tail > 0 && (
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} strokeLinecap="butt"
            className={wsTone(accentTone)} style={{ stroke: 'var(--tone)' }}
            strokeDasharray={`${(c * tail) / 100} ${c}`} strokeDashoffset={-(c * main) / 100}
          />
        )}
        {main > 0 && (
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none" style={{ stroke: 'var(--ws-ink)' }} strokeWidth={stroke}
            strokeDasharray={`${(c * main) / 100} ${c}`}
          />
        )}
      </svg>
      <span>{label}</span>
    </span>
  );
};

export interface WsBarcodeSegment { tone: WsTone; count: number }

/**
 * 바코드 — 한 줄이 k건. 구간마다 색이 다르고, capacity 를 못 채운 나머지는 옅은 줄.
 * 건수가 capacity 보다 많으면 한 줄이 여러 건을 뜻한다(perMark 로 돌려준다 — 범례에 쓰라고).
 */
export function wsBarcodeMarks(segments: WsBarcodeSegment[], capacity: number, scaleTotal?: number) {
  const total = segments.reduce((s, g) => s + Math.max(0, g.count), 0);
  const basis = Math.max(total, scaleTotal ?? 0);
  const perMark = Math.max(1, Math.ceil(basis / capacity));
  const marks: Array<WsTone | null> = [];
  segments.forEach((g) => {
    const n = g.count > 0 ? Math.max(1, Math.round(g.count / perMark)) : 0;
    for (let i = 0; i < n; i += 1) marks.push(g.tone);
  });
  while (marks.length < capacity) marks.push(null);
  return { marks: marks.slice(0, Math.max(capacity, marks.length)), perMark, total };
}

export const WsBarcode: React.FC<{
  segments: WsBarcodeSegment[];
  capacity?: number;
  /** 여러 바코드를 같은 눈금으로 비교할 때 — 가장 큰 쪽의 건수 */
  scaleTotal?: number;
  size?: 'sm' | 'md';
  label?: string;
}> = ({ segments, capacity = 120, scaleTotal, size = 'md', label }) => {
  const { marks } = wsBarcodeMarks(segments, capacity, scaleTotal);
  return (
    <div className={`ws-barcode ${size === 'sm' ? 'ws-barcode--sm' : ''}`} role="img" aria-label={label}>
      {marks.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <i key={i} className={t ? wsTone(t) : undefined} data-muted={t ? undefined : 'true'} />
      ))}
    </div>
  );
};

/** 한 칸이 한 건인 칸 지도 */
export const WsCells: React.FC<{ segments: WsBarcodeSegment[]; max?: number; label?: string }> = ({ segments, max = 120, label }) => {
  const cells: WsTone[] = [];
  segments.forEach((g) => { for (let i = 0; i < g.count && cells.length < max; i += 1) cells.push(g.tone); });
  return (
    <div className="ws-cells" role="img" aria-label={label}>
      {cells.map((t, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <i key={i} className={`ws-cell ${t === 'gray' ? '' : wsTone(t)}`} data-muted={t === 'gray' ? 'true' : undefined} />
      ))}
    </div>
  );
};
